<template>
  <SalesPlayDrawerCard>
    <div class="drawer-section">
      <div class="drawer-sectionLabel">PICK A CONTACT SEGMENT</div>
      <div class="drawer-mutedText">
        Pick a Segment from Hubspot that contains the contacts
        you are interested in
      </div>
      <SearchSelect
        :model-value="localSegmentId"
        :options="initialOption"
        :options-function="searchSegments"
        placeholder="Choose a Segment..."
        search-placeholder="Search Segments by name..."
        :allow-clear="false"
        @update:model-value="onSegmentSelected"
      />
      <label v-if="allowDirectIdInput">
        <span class="fieldLabel">Segment Id</span>
        <input class="input" v-model="localSegmentId" @update:model-value="onSegmentSelected" />
      </label>
      <div v-if="validating" class="drawer-mutedText">Checking Segment…</div>
      <div v-else-if="validationError" class="drawer-errorText">{{ validationError }}</div>
      <div v-else-if="validationSuccess" class="drawer-successText">{{ validationSuccess }}</div>
    </div>

    <div v-if="previewError" class="drawer-section">
      <div class="drawer-errorText">{{ previewError }}</div>
    </div>

  </SalesPlayDrawerCard>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SalesPlayDrawerCard from './SalesPlayDrawerCard.vue'
import SearchSelect from '../../../components/SearchSelect.vue'
import ToggleSwitch from '../../../components/ToggleSwitch.vue'
import { apiFetch } from '../../../services/api'
import { useSalesPlayDraft } from '../../composables/useSalesPlayDraft'
import { useBlockValidation } from '../../composables/useBlockValidation'

const props = defineProps<{
  playId: string
  pipelineId: string | null
  nodeIndex: number
}>()

const { nodes, updateNodeConfig } = useSalesPlayDraft()
const { validating, validationError, validationSuccess, validate: runValidation, reset: resetValidation } = useBlockValidation()

// ── Local working copy ──────────────────────────────────────────

const currentConfig = () => nodes.value[props.nodeIndex]?.config || {}

const localSegmentId = ref(currentConfig().segment_id || '')
const localSegmentName = ref(currentConfig().segment_name || '')

const allowDirectIdInput = ref(false)

function applyConfig() {
  const currentName = currentConfig().name
  updateNodeConfig(props.nodeIndex, {
    type: 'hubspot_champions_search',
    name: currentName || localSegmentName.value,
    segment_id: localSegmentId.value,
    segment_name: localSegmentName.value,
  })
}

function resetLocal() {
  const cfg = currentConfig()
  localSegmentId.value = cfg.segment_id || ''
  localSegmentName.value = cfg.segment_name || ''
  previewRecords.value = []
  previewError.value = ''
  resetValidation()
}

// Re-sync when the draft node changes (e.g. after canvas-level cancel).
watch(() => nodes.value[props.nodeIndex]?.config, () => {
  resetLocal()
}, { deep: true })

// Provide the current selection so SearchSelect can show name + ID at rest.
const initialOption = computed(() => {
  if (!localSegmentId.value) return []
  return [{
    value: localSegmentId.value,
    label: localSegmentName.value || localSegmentId.value,
    secondaryLabel: localSegmentId.value,
  }]
})

// ── Report search via SearchSelect ──────────────────────────────

let lastFetchedOptions: { value: string; label: string }[] = []

async function searchSegments(query: string) {
  const qs = query ? `?search=${encodeURIComponent(query)}` : ''
  try {
    const resp: any = await apiFetch(`/integrations/hubspot/segments${qs}`)
    const segments = resp?.segments || []
    const opts = segments.map((r: any) => ({
      value: r.id,
      label: r.name,
      secondaryLabel: r.id,
    }))
    lastFetchedOptions = opts
    allowDirectIdInput.value = (opts.length === 0)
    return opts
  } catch (error) {
    allowDirectIdInput.value = true
    console.error(error)
    return []
  }
}

function onSegmentSelected(segmentId: string) {
  if (!segmentId) return
  localSegmentId.value = segmentId
  const match = lastFetchedOptions.find((o) => o.value === segmentId)
  if (match) localSegmentName.value = match.label
  runValidation('hubspot_champions_search', {
    type: 'hubspot_champions_search',
    segment_id: segmentId,
    segment_name: localSegmentName.value,
  })
  applyConfig()
}

// ── Preview ─────────────────────────────────────────────────────

const previewing = ref(false)
const previewError = ref('')
const previewRecords = ref<any[]>([])
const previewTotalSize = ref(0)
const previewTruncated = ref(false)

</script>

